// M148 — Ads-voorstellen tab in /content.
// Kant-en-klare Ads-wijzigingen die de klant zelf toepast in Google Ads.
// Zelfde pattern als BerichtenPage — geen echte Ads-API.
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useEmbed } from "@/contexts/EmbedContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Megaphone, Loader2, Copy, Check, Undo2, Plus, Trash2, MapPin,
} from "lucide-react";
import _brand from "@/utils/normalizeBrand";

const TYPE_LABEL = {
  kopregel: "Kopregel",
  zoekwoord_toevoegen: "Zoekwoord toevoegen",
  zoekwoord_uitsluiten: "Zoekwoord uitsluiten",
  bod_verlagen: "Bod verlagen",
  bod_verhogen: "Bod verhogen",
  campagne_pauzeren: "Campagne pauzeren",
  campagne_hervatten: "Campagne hervatten",
  budget_wijzigen: "Budget wijzigen",
};

function TypeBadge({ type }) {
  const label = TYPE_LABEL[type] || "Wijziging";
  return (
    <span
      className="inline-block text-xs font-semibold rounded-full px-2.5 py-1"
      style={{ background: "#E8F5EF", color: "#087F8C", letterSpacing: "0.03em" }}
      data-testid={`ads-type-badge-${type}`}
    >
      {label}
    </span>
  );
}

function CopyBlock({ block, index, proposalId, onCopied }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(block.tekst);
      setCopied(true);
      onCopied?.();
      toast.success("Tekst gekopieerd");
      setTimeout(() => setCopied(false), 1600);
    } catch (_e) {
      toast.error("Kopiëren lukte niet");
    }
  };
  return (
    <div
      className="rounded-[12px] p-3 flex items-start gap-3"
      style={{ background: "#F7F8F5" }}
      data-testid={`ads-copyblock-${proposalId}-${index}`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-text-muted mb-1">{block.label}</div>
        <div className="text-sm text-text-primary whitespace-pre-wrap font-sans leading-relaxed break-words">
          {block.tekst}
        </div>
      </div>
      <button
        onClick={copy}
        aria-label={`Kopieer ${block.label}`}
        data-testid={`ads-copyblock-btn-${proposalId}-${index}`}
        className="shrink-0 inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-[8px] border border-border-hairline text-accent-teal-dim hover:bg-bg-panel hover:border-accent-teal/40"
      >
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        {copied ? "Gekopieerd" : "Kopieer"}
      </button>
    </div>
  );
}

function ProposalCard({ p, index, onDone, onUndo, onDelete, onCopied }) {
  const isMeasuring = p.status === "measuring";
  return (
    <div
      className="bg-bg-panel rounded-[16px] p-6 flex flex-col gap-4"
      style={{ boxShadow: "0 1px 3px rgba(23,44,50,0.06)" }}
      data-testid={`ads-proposal-card-${index}`}
    >
      <div className="flex items-start justify-between gap-3">
        <TypeBadge type={p.proposal_type} />
        <button
          onClick={() => onDelete(p.proposal_id)}
          aria-label="Voorstel verwijderen"
          data-testid={`ads-proposal-delete-${index}`}
          className="text-text-muted hover:text-warn-amber p-1"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <h3 className="text-base md:text-lg font-bold text-text-primary leading-snug">
        {_brand(p.wat_wijzigen_nl)}
      </h3>

      <div>
        <div className="text-xs font-semibold text-text-muted mb-1 inline-flex items-center gap-1">
          <MapPin className="w-3 h-3 text-accent-teal" aria-hidden="true" />
          Waar in Google Ads
        </div>
        <p className="text-sm text-text-primary leading-relaxed">{_brand(p.waar_in_ads_nl)}</p>
      </div>

      <div className="rounded-[12px] p-3 space-y-1.5 bg-accent-mint">
        <div className="text-xs font-semibold text-accent-teal-dim">Waarom dit werkt</div>
        {p.huidige_situatie_nl && (
          <div className="text-sm text-text-primary"><span className="text-text-muted">Nu:</span> {_brand(p.huidige_situatie_nl)}</div>
        )}
        {p.verwachte_uitkomst_nl && (
          <div className="text-sm text-text-primary"><span className="text-text-muted">Verwachting:</span> {_brand(p.verwachte_uitkomst_nl)}</div>
        )}
        {p.impact_schatting_nl && (
          <div className="text-sm text-text-primary"><span className="text-text-muted">Impact:</span> {_brand(p.impact_schatting_nl)}</div>
        )}
      </div>

      {p.copy_blokken?.length > 0 && (
        <div className="space-y-2">
          {p.copy_blokken.map((b, i) => (
            <CopyBlock
              key={i}
              block={b}
              index={i}
              proposalId={p.proposal_id}
              onCopied={() => onCopied(p.proposal_id)}
            />
          ))}
        </div>
      )}

      {p.data_bronnen?.length > 0 && (
        <div className="text-xs text-text-subtle italic">
          Bron: {p.data_bronnen.map(_brand).join(", ")}
        </div>
      )}

      <div className="mt-auto pt-2 flex flex-wrap gap-2">
        {isMeasuring ? (
          <button
            onClick={() => onUndo(p.proposal_id)}
            data-testid={`ads-proposal-undo-${index}`}
            className="inline-flex items-center gap-1.5 rounded-[10px] px-4 py-2 border border-border-hairline text-text-muted hover:bg-bg-canvas text-sm"
          >
            <Undo2 className="w-3.5 h-3.5" /> Toch niet
          </button>
        ) : (
          <button
            onClick={() => onDone(p.proposal_id)}
            data-testid={`ads-proposal-done-${index}`}
            className="inline-flex items-center gap-1.5 rounded-[10px] px-4 py-2 bg-accent-teal text-white text-sm font-medium hover:bg-accent-teal-dim"
            style={{ boxShadow: "0 1px 3px rgba(8,127,140,0.24)" }}
          >
            <Check className="w-3.5 h-3.5" /> Ik heb dit gedaan
          </button>
        )}
        <button
          onClick={() => onDelete(p.proposal_id)}
          className="inline-flex items-center gap-1.5 rounded-[10px] px-3 py-2 text-sm text-text-muted hover:text-warn-amber"
        >
          Laat maar
        </button>
        {isMeasuring && (
          <span className="text-xs text-text-muted italic self-center">In meting…</span>
        )}
      </div>
    </div>
  );
}

export default function AdsVoorstellenPage() {
  const { hideInnerHeader } = useEmbed();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/ads-proposals/gallery");
      setItems(data.items || []);
      setErr(null);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message_nl
        || e?.response?.data?.detail
        || "Kon Ads-voorstellen niet laden.";
      setItems([]);
      setErr(typeof msg === "string" ? msg : "Kon Ads-voorstellen niet laden.");
    }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post("/ads-proposals/generate");
      toast.success("Nieuwe voorstellen gemaakt");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      const msg = detail.message_nl || detail || "Voorstellen maken lukte niet.";
      toast.error(typeof msg === "string" ? msg : "Voorstellen maken lukte niet.");
      setErr(typeof msg === "string" ? msg : null);
    } finally {
      setBusy(false);
    }
  };

  const onDone = async (id) => {
    try { await api.post(`/ads-proposals/${id}/done`); await load(); }
    catch (_e) { toast.error("Kon niet als gedaan markeren."); }
  };
  const onUndo = async (id) => {
    try { await api.post(`/ads-proposals/${id}/undo`); await load(); }
    catch (_e) { toast.error("Kon niet terugdraaien."); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Voorstel verwijderen?")) return;
    try { await api.delete(`/ads-proposals/${id}`); await load(); }
    catch (_e) { toast.error("Verwijderen lukte niet."); }
  };
  const onCopied = async (id) => {
    try { await api.post(`/ads-proposals/${id}/mark-copied`); } catch (_e) {}
  };

  const visible = (items || []).filter((p) => p.status !== "gearchiveerd");

  const body = (
    <>
      <div className="mb-6 flex justify-end">
        <button
          onClick={generate}
          disabled={busy}
          data-testid="ads-proposals-generate-btn"
          className="inline-flex items-center gap-1.5 rounded-[10px] px-4 py-2 bg-accent-teal text-white text-sm font-medium hover:bg-accent-teal-dim disabled:opacity-60"
          style={{ boxShadow: "0 1px 3px rgba(8,127,140,0.24)" }}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Maak nieuwe voorstellen
        </button>
      </div>

      {err && (
        <div className="mb-4 rounded-[12px] p-4 bg-warn-amber/10 border border-warn-amber/30 text-sm text-text-primary"
              data-testid="ads-proposals-err">{err}</div>
      )}

      {items === null && (
        <div className="flex items-center gap-2 text-text-muted"
              role="status" aria-live="polite">
          <Loader2 className="w-4 h-4 animate-spin" /> Voorstellen laden…
        </div>
      )}

      {items !== null && visible.length === 0 && !err && (
        <div className="rounded-[16px] border border-dashed border-border-hairline bg-bg-panel p-10 text-center"
              data-testid="ads-proposals-empty">
          <div className="w-12 h-12 mx-auto rounded-full bg-accent-mint flex items-center justify-center mb-4">
            <Megaphone className="w-5 h-5 text-accent-teal" aria-hidden="true" />
          </div>
          <p className="text-base text-text-primary mb-2 font-medium">Nog geen voorstellen</p>
          <p className="text-sm text-text-muted max-w-md mx-auto mb-4">
            Zodra JustEasySEO je merkstem kent verschijnen hier voorstellen.
            Vul eerst je Bedrijfsprofiel en Merkstem aan.
          </p>
          <button
            onClick={generate}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-[10px] px-5 py-2.5 bg-accent-teal text-white text-sm font-medium hover:bg-accent-teal-dim disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Maak je eerste voorstellen
          </button>
        </div>
      )}

      {visible.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5"
              data-testid="ads-proposals-grid">
          {visible.map((p, i) => (
            <ProposalCard
              key={p.proposal_id}
              p={p}
              index={i}
              onDone={onDone}
              onUndo={onUndo}
              onDelete={onDelete}
              onCopied={onCopied}
            />
          ))}
        </div>
      )}
    </>
  );

  if (hideInnerHeader) return body;

  return (
    <AppLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary tracking-tight flex items-center gap-2"
             data-testid="ads-proposals-title">
          <Megaphone className="w-5 h-5 text-accent-teal" aria-hidden="true" /> Advertentie-voorstellen
        </h1>
        <p className="text-sm text-text-muted mt-1">
          Kant-en-klare Ads-wijzigingen. Kopieer en pas zelf toe in Google Ads.
        </p>
      </div>
      {body}
    </AppLayout>
  );
}
